'use strict';

// "Are the fields open today?" on the sign-in page: a one-line pill with a
// traffic-light dot, opening a modal with every field's state. Reads the same
// login-free JSON the /status page renders, so the two can never disagree.

import { apiFetch } from './api.js';
import { el, showModal, closeBtn } from './dom.js';

// The server's tone → the dot's color class. Unknown tones read as muted
// rather than guessing a color.
const TONES = new Set(['open', 'mixed', 'closed', 'muted']);

const FIELD_STATE_LABEL = { open: 'Open', partial: 'Partly closed', closed: 'Closed' };

/**
 * The pill's wording for a field-status payload. `text` is short enough for
 * one line on a phone; `cta` invites a tap when there is something to read.
 * Mixed days get a plain "some fields" line instead of the server's
 * count-by-state summary, which is too long for a pill — the modal shows it.
 */
export function statusPill(data) {
  const tone = TONES.has(data?.tone) ? data.tone : 'muted';
  const summary = data?.summary || 'Field status';
  switch (tone) {
    case 'open':   return { tone, text: summary, cta: '' };
    case 'mixed':  return { tone, text: 'Some fields are closed today', cta: 'See details' };
    case 'closed': return { tone, text: summary, cta: 'Details' };
    default:       return { tone, text: summary, cta: '' };
  }
}

/** "Monday, September 28" from a YYYY-MM-DD date, without a timezone shift. */
export function statusDateLabel(ymd) {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function fieldRow(f) {
  const windows = (f.closed_windows || []).join(', ');
  const reasons = (f.reasons || []).join(' · ');
  return el('li', { className: 'pstatus-field' },
    el('span', { className: 'pstatus-dot pstatus-dot-' + f.state, 'aria-hidden': 'true' }),
    el('div', { className: 'pstatus-field-body' },
      el('div', { className: 'pstatus-field-name' }, f.name),
      el('div', { className: 'pstatus-field-state pstatus-state-' + f.state },
        (FIELD_STATE_LABEL[f.state] || f.state) + (windows ? ': ' + windows : '')),
      reasons ? el('div', { className: 'pstatus-field-reason' }, reasons) : null,
    ),
  );
}

function openStatusModal(data) {
  const { tone } = statusPill(data);
  const fields = data.fields || [];
  const updated = data.generated_at
    ? new Date(data.generated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'
    : '';
  showModal(el('div', { className: 'pstatus-modal' },
    el('div', { className: 'modal-header' },
      el('h3', {}, 'Field Status'),
      closeBtn(),
    ),
    el('div', { className: 'pstatus-modal-body' },
      el('div', { className: 'pstatus-date' }, statusDateLabel(data.date)),
      el('div', { className: 'pstatus-summary pstatus-summary-' + tone },
        el('span', { className: 'pstatus-dot pstatus-dot-' + tone, 'aria-hidden': 'true' }),
        el('div', {},
          el('div', { className: 'pstatus-summary-text' }, data.summary || ''),
          data.season ? el('div', { className: 'pstatus-season' }, data.season) : null,
        ),
      ),
      fields.length > 0
        ? el('ul', { className: 'pstatus-fields' }, ...fields.map(fieldRow))
        : null,
    ),
    el('div', { className: 'modal-footer pstatus-footer' },
      el('span', { className: 'pstatus-updated' }, updated ? 'Updated ' + updated : ''),
      el('a', { href: '/status', target: '_blank', rel: 'noopener', className: 'btn btn-outline btn-sm' }, 'Shareable page ↗'),
    ),
  ));
}

/**
 * The pill, empty until the status arrives. It stays hidden if the request
 * fails: a sign-in page with no status line is better than one with a wrong one.
 */
export function publicStatusPill() {
  const pill = el('button', { type: 'button', className: 'pstatus-pill', hidden: true });
  apiFetch('GET', '/public/field-status').then(data => {
    if (!data || !pill.isConnected) return;
    const { tone, text, cta } = statusPill(data);
    // replaceChildren renders a null child as the text "null", so the
    // optional call to action is filtered out.
    pill.replaceChildren(...[
      el('span', { className: 'pstatus-dot pstatus-dot-' + tone, 'aria-hidden': 'true' }),
      el('span', { className: 'pstatus-pill-text' }, text),
      cta ? el('span', { className: 'pstatus-pill-cta' }, cta) : null,
      el('span', { className: 'pstatus-pill-chevron', 'aria-hidden': 'true' }, '›'),
    ].filter(Boolean));
    pill.setAttribute('aria-label', text + ' — show field status');
    pill.onclick = () => openStatusModal(data);
    pill.hidden = false;
  }).catch(() => { /* no status line rather than a wrong one */ });
  return pill;
}
